import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { ScrollView, StyleSheet, TouchableOpacity } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { SheetScreen } from '@/components/ScreenComponent';
import { ScreenNavBar } from '@/components/ScreenNavBar';
import { Text } from '@/components/TextComponent';
import { Box, Column, Row } from '@/components/lib/components';
import { ds } from '@/components/lib/design.system';
import { useGetMysteryMultipliersFrequencyQuery } from '@/feature/betslip-pickem/api/query.generated';
import { useBetslipStore } from '@/feature/betslip-pickem/hooks/use-betslip-store';
import { useOpenTermsLink } from '@/feature/terms-and-conditions/hooks/use-open-terms-link';
import { common, withOpacity } from '@/styles/styles';

import { LabelDividerRow } from '../components/LabelDividerRow';
import { PayoutLine } from '../components/PayoutLine';

export const MysteryInfoModal = () => {
    const { t } = useTranslation('common');
    const validationData = useBetslipStore(state => state.validationData);
    const openTermsLink = useOpenTermsLink();

    // Fetch mystery multipliers frequency data from GraphQL
    const [{ data: mysteryFrequencyData }] = useGetMysteryMultipliersFrequencyQuery();

    const mysteryData = validationData.MYSTERY?.betValidation;

    const multiplierRange =
        mysteryData?.minMysteryMultiplier && mysteryData?.maxMysteryMultiplier
            ? `${mysteryData.minMysteryMultiplier}x-${mysteryData.maxMysteryMultiplier}x`
            : '2x-1000x';

    // Transform GraphQL data to display format
    const multiplierData = useMemo(() => {
        if (!mysteryFrequencyData?.getMysteryMultipliersFrequency?.multipliersFrequency) {
            return null; // No fallback data - will show error state
        }

        const totalWeight = mysteryFrequencyData.getMysteryMultipliersFrequency.totalWeight;

        return mysteryFrequencyData.getMysteryMultipliersFrequency.multipliersFrequency
            .map(item => ({
                multiplier: `${item.multiplier}`,
                frequency: `${Math.round(item.frequency).toLocaleString()} in ${Math.round(
                    totalWeight
                ).toLocaleString()}`,
            }))
            .sort((a, b) => parseFloat(b.multiplier) - parseFloat(a.multiplier)); // Sort by multiplier value descending
    }, [mysteryFrequencyData]);

    const insets = useSafeAreaInsets();

    return (
        <SheetScreen style={common.flex} edges={['top']}>
            <Box flex={1} backgroundColor={ds.colorToken.background.neutral.base}>
                <ScreenNavBar
                    sheet
                    title={t('mystery_multiplier')}
                    closeIconMode={'close'}
                    titleOverrideStyle={styles.title}
                />

                <ScrollView style={styles.scrollView} showsVerticalScrollIndicator={false}>
                    <Column paddingHorizontal={'s16'} style={{ paddingBottom: insets.bottom + 48 }}>
                        {/* Header text */}
                        <Box marginTop={'s16'} marginBottom={'s16'}>
                            <Text
                                variant={'bodyMedium'}
                                color={ds.colorToken.text.primary}
                                textAlign={'left'}
                                lineHeight={24}
                                letterSpacing={-0.23}
                            >
                                {MYSTERY_INFO_TEXT.description}
                            </Text>
                            <Row marginTop={'s16'}>
                                <Text variant={'bodyMedium'} color={ds.colorToken.text.primary}>
                                    {MYSTERY_INFO_TEXT.cashEntriesOnly}
                                </Text>
                                <TouchableOpacity onPress={openTermsLink} activeOpacity={0.8}>
                                    <Text
                                        variant={'bodyMedium'}
                                        color={ds.colorToken.text.primary}
                                        style={styles.termsAndConditions}
                                    >
                                        {MYSTERY_INFO_TEXT.termsAndConditions}
                                    </Text>
                                </TouchableOpacity>
                                <Text variant={'bodyMedium'} color={ds.colorToken.text.primary}>
                                    {' apply.'}
                                </Text>
                            </Row>
                        </Box>

                        {/* How it works section */}
                        <Box marginTop={'s8'} marginBottom={'s24'}>
                            <Box
                                style={styles.container}
                                backgroundColor={ds.colorToken.background.neutral.subtler}
                                borderRadius={'r16'}
                                paddingBottom={'s4'}
                            >
                                <Text variant={'titleLarge'} color={ds.colorToken.text.primary} marginBottom={'s20'}>
                                    {MYSTERY_INFO_TEXT.howItWorksTitle}
                                </Text>
                                {MYSTERY_INFO_TEXT.steps.map((stepText, index) => (
                                    <Row key={index} paddingBottom={'s16'} alignItems={'flex-start'}>
                                        <Box
                                            backgroundColor={ds.colorToken.background.neutral.inverse}
                                            width={24}
                                            height={24}
                                            borderRadius={'r20'}
                                            alignItems={'center'}
                                            justifyContent={'center'}
                                            marginRight={'s16'}
                                        >
                                            <Text
                                                variant={'labelMedium'}
                                                color={'neutral900'}
                                                style={styles.stepNumber}
                                            >
                                                {index + 1}
                                            </Text>
                                        </Box>
                                        <Text
                                            style={styles.textShrink}
                                            variant={'bodyMedium'}
                                            color={ds.colorToken.text.primary}
                                        >
                                            {stepText.includes('{{multiplierRange}}')
                                                ? stepText.replace('{{multiplierRange}}', multiplierRange)
                                                : stepText}
                                        </Text>
                                    </Row>
                                ))}
                            </Box>
                        </Box>
                        {/* Multiplier breakdown */}
                        <Box
                            borderRadius={'r16'}
                            backgroundColor={ds.colorToken.background.neutral.subtler}
                            borderWidth={1}
                            style={styles.container}
                            pb={'s12'}
                        >
                            <Text variant={'titleLarge'} color={ds.colorToken.text.primary} paddingBottom={'s8'}>
                                {MYSTERY_INFO_TEXT.multiplierBreakdownTitle}
                            </Text>
                            <LabelDividerRow leftText={'Multiplier'} rightText={'Frequency'} />
                            {(multiplierData ?? []).map((item, index) => (
                                <PayoutLine
                                    key={index}
                                    multiplier={item.multiplier}
                                    rightLabel={item.frequency}
                                    dollarSignsNumber={0}
                                />
                            ))}
                        </Box>
                    </Column>
                </ScrollView>
            </Box>
        </SheetScreen>
    );
};

const styles = StyleSheet.create({
    title: {
        color: ds.colorValue.text.primary,
    },
    scrollView: {
        flex: 1,
    },
    stepNumber: {
        fontWeight: '700',
    },
    textShrink: {
        flexShrink: 1,
    },
    container: {
        borderColor: ds.colorValue.border.subtle,
        paddingHorizontal: 16,
        paddingTop: 16,
    },
    multiplierBadge: {
        backgroundColor: withOpacity(ds.colorValue.background.brand.emphasis, 0.15),
        paddingHorizontal: 12,
        paddingVertical: 6,
        borderRadius: 8,
        minWidth: 56,
        alignItems: 'center',
        justifyContent: 'center',
    },
    termsAndConditions: {
        textDecorationLine: 'underline',
        textDecorationStyle: 'solid',
    },
});

// Constants for text content
const MYSTERY_INFO_TEXT = {
    description:
        'Mystery Multiplier can boost your entry up to 1000x with just 2 picks — it is only available for regular projections (no alternate or promo picks allowed).',
    cashEntriesOnly: 'Cash entries only. ',
    termsAndConditions: 'Terms & Conditions',
    howItWorksTitle: 'How it works',
    steps: [
        'Create a 2-pick lineup (regular picks only)',
        'Apply the Mystery Multiplier promo',
        'Place your entry to start the wheel',
        'A multiplier of {{multiplierRange}} will be revealed',
    ],
    multiplierBreakdownTitle: 'Multiplier breakdown',
    multiplierLabel: 'Multiplier',
    frequencyLabel: 'Frequency',
    errorMessage: 'Unable to load multiplier data',
} as const;
